import { forwardRef, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from 'react';
import { cn } from '@core/utils/cn';

export interface ShimmerButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Colour of the beam that travels around the edge. */
  readonly shimmerColor?: string;
  /** Thickness of the animated border ring. */
  readonly shimmerSize?: string;
  /** Corner radius of the button. */
  readonly borderRadius?: string;
  /** Time for one full loop of the beam around the edge. */
  readonly shimmerDuration?: string;
  /** Button fill colour. */
  readonly background?: string;
  readonly className?: string;
  readonly children?: ReactNode;
}

/**
 * Button with a light beam that loops continuously around its edge.
 * The beam is a rotating conic-gradient border driven by the `.dd-loop-btn`
 * rules in `src/styles/globals.css` (Tailwind v4 is CSS-first — there is no
 * tailwind.config.js). Every knob is passed down as a CSS variable, and the
 * animation rests on its start frame so the global reduced-motion rule
 * collapses it to a static border.
 */
export const ShimmerButton = forwardRef<HTMLButtonElement, ShimmerButtonProps>(
  function ShimmerButton(
    {
      shimmerColor = '#ffffff',
      shimmerSize = '2px',
      shimmerDuration = '2.5s',
      borderRadius = '100px',
      background = 'rgba(0, 0, 0, 1)',
      className,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        style={
          {
            '--dd-beam': shimmerColor,
            '--dd-thick': shimmerSize,
            '--dd-speed': shimmerDuration,
            '--dd-radius': borderRadius,
            '--dd-bg': background,
          } as CSSProperties
        }
        className={cn(
          'dd-loop-btn relative isolate inline-flex cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap border-0 px-6 py-3 text-white',
          '[background:var(--dd-bg)] [border-radius:var(--dd-radius)]',
          'transform-gpu transition-transform duration-200 ease-in-out active:translate-y-px',
          className,
        )}
        {...props}
      >
        <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
      </button>
    );
  },
);
